import { useState, useEffect } from "react";
import { Globe, Home as HomeIcon, Search, ImagePlus, Plus, Trash2, ArrowUp, ArrowDown, Layers, Newspaper, Pencil, X, Eye } from "lucide-react";
import { adminApi } from "./api";
import { toast } from "sonner";
import CmsSectionEditor from "./CmsSectionEditor";
import EmiTickerPreview from "./EmiTickerPreview";

const TABS = [
  { key: "global", label: "Global Content", icon: Globe },
  { key: "home", label: "Home", icon: HomeIcon },
  { key: "pages", label: "Product Pages", icon: Layers },
  { key: "journal", label: "Journal / Blog", icon: Newspaper },
  { key: "seo", label: "SEO & Metadata", icon: Search },
];

export default function CmsModule() {
  const [tab, setTab] = useState("global");

  return (
    <div className="p-5 lg:p-8 max-w-[1100px]">
      <header className="mb-8">
        <p className="text-[10.5px] tracking-[0.28em] uppercase mb-2" style={{ color: "var(--ax-text-muted)" }}>
          Website Content
        </p>
        <h2 className="ax-serif text-3xl">Manage what visitors see — without touching code</h2>
        <div className="flex flex-wrap gap-2 mt-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`ax-btn ${tab === t.key ? "ax-btn-gold" : "ax-btn-ghost"}`}
              onClick={() => setTab(t.key)}
              data-testid={`cms-tab-${t.key}`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      {tab === "global" && <GlobalContentTab />}
      {tab === "home" && <HomeTab />}
      {tab === "pages" && <ProductPagesTab />}
      {tab === "journal" && <JournalTab />}
      {tab === "seo" && <SeoTab />}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Shared field primitives                                                 */
/* ---------------------------------------------------------------------- */

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="ax-label">{label}</label>
      {children}
      {hint && (
        <p className="text-[11px] mt-1.5" style={{ color: "var(--ax-text-muted)" }}>
          {hint}
        </p>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, testId, hint, placeholder }) {
  return (
    <Field label={label} hint={hint}>
      <input
        className="ax-input"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </Field>
  );
}

function TextAreaField({ label, value, onChange, testId, hint, rows = 3 }) {
  return (
    <Field label={label} hint={hint}>
      <textarea
        className="ax-input"
        style={{ height: "auto" }}
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </Field>
  );
}

function ToggleField({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2.5 text-sm cursor-pointer select-none">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      {label}
    </label>
  );
}

function ImageField({ label, value, onChange, testId, hint }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await adminApi.post("/cms/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${process.env.REACT_APP_BACKEND_URL}${r.data.image_url}`);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Field label={label} hint={hint}>
      <div className="flex items-start gap-4">
        <div
          className="w-28 h-20 rounded-lg border flex items-center justify-center overflow-hidden shrink-0"
          style={{ borderColor: "var(--ax-border)", background: "rgba(255,255,255,0.02)" }}
        >
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
          ) : (
            <ImagePlus className="w-4 h-4" style={{ color: "var(--ax-text-muted)" }} />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            className="ax-input"
            placeholder="Image URL, or upload below"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
          />
          <div className="flex items-center gap-2">
            <label className="ax-btn ax-btn-ghost cursor-pointer" data-testid={`${testId}-upload-btn`}>
              {uploading ? "Uploading…" : "Upload image"}
              <input type="file" accept="image/*" className="hidden" data-testid={`${testId}-upload-input`} onChange={(e) => upload(e.target.files?.[0])} />
            </label>
            {value && (
              <button type="button" className="ax-btn ax-btn-ghost" onClick={() => onChange("")} data-testid={`${testId}-remove-btn`}>
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            )}
          </div>
        </div>
      </div>
    </Field>
  );
}

/* ---------------------------------------------------------------------- */
/* GLOBAL CONTENT                                                          */
/* ---------------------------------------------------------------------- */

const NAV_KEYS = [
  { key: "home", label: "Home" },
  { key: "dynamic-series", label: "Dynamic Series" },
  { key: "value-packs", label: "Value Packs" },
  { key: "shop", label: "Shop" },
  { key: "about", label: "About" },
  { key: "contact", label: "Contact" },
];

function GlobalContentTab() {
  const [sub, setSub] = useState("header");
  const subs = [
    { key: "header", label: "Header" },
    { key: "announcement_banner", label: "Announcement Banner" },
    { key: "footer", label: "Footer" },
    { key: "contact", label: "Contact" },
  ];
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        {subs.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`ax-btn ${sub === s.key ? "ax-btn-gold" : "ax-btn-ghost"}`}
            onClick={() => setSub(s.key)}
            data-testid={`cms-subtab-global-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {sub === "header" && <HeaderEditor />}
      {sub === "announcement_banner" && <AnnouncementBannerEditor />}
      {sub === "footer" && <FooterEditor />}
      {sub === "contact" && <ContactEditor />}
    </div>
  );
}

const MEGA_MENU_KEYS = [
  { key: "products", defaultLabel: "Products" },
  { key: "solutions", defaultLabel: "Solutions" },
  { key: "locations", defaultLabel: "Locations" },
];

function HeaderEditor() {
  return (
    <CmsSectionEditor
      page="global"
      section="header"
      title="Header"
      description="Nav links, phone display, the header CTA button, and the Products/Solutions/Locations mega-menu triggers."
      renderFields={(draft, setField) => {
        const navItems = Array.isArray(draft.nav_items) && draft.nav_items.length > 0
          ? draft.nav_items
          : NAV_KEYS.map((n) => ({ ...n, enabled: true }));
        const setNavItem = (key, patch) => {
          const next = NAV_KEYS.map((n) => {
            const existing = navItems.find((i) => i.key === n.key) || { ...n, enabled: true };
            return n.key === key ? { ...existing, ...patch } : existing;
          });
          setField("nav_items", next);
        };
        const megaMenus = draft.mega_menus || {};
        const setMegaMenu = (key, patch) => {
          const current = MEGA_MENU_KEYS.reduce((acc, m) => {
            acc[m.key] = { label: megaMenus[m.key]?.label || m.defaultLabel, enabled: megaMenus[m.key]?.enabled !== false };
            return acc;
          }, {});
          current[key] = { ...current[key], ...patch };
          setField("mega_menus", current);
        };
        return (
          <>
            <TextField label="Header CTA button text" value={draft.cta_label} onChange={(v) => setField("cta_label", v)} testId="cms-header-cta-label" placeholder="Experience" />
            <TextField label="Header CTA button link" value={draft.cta_link} onChange={(v) => setField("cta_link", v)} testId="cms-header-cta-link" placeholder="/experience" />
            <Field label="Navigation items" hint="Edit labels/links or hide simple nav items.">
              <div className="space-y-2">
                {NAV_KEYS.map((n) => {
                  const item = navItems.find((i) => i.key === n.key) || { ...n, enabled: true };
                  return (
                    <div key={n.key} className="flex items-center gap-3 flex-wrap p-3 rounded-lg" style={{ border: "1px solid var(--ax-border)" }}>
                      <ToggleField
                        label={n.label}
                        checked={item.enabled !== false}
                        onChange={(v) => setNavItem(n.key, { enabled: v })}
                        testId={`cms-nav-enabled-${n.key}`}
                      />
                      <input
                        className="ax-input flex-1 min-w-[140px]"
                        value={item.label}
                        onChange={(e) => setNavItem(n.key, { label: e.target.value })}
                        data-testid={`cms-nav-label-${n.key}`}
                      />
                      <input
                        className="ax-input flex-1 min-w-[140px]"
                        value={item.to}
                        onChange={(e) => setNavItem(n.key, { to: e.target.value })}
                        data-testid={`cms-nav-link-${n.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </Field>
            <Field label="Mega-menu triggers" hint="Relabel or hide the Products/Solutions/Locations dropdown menus. Their link contents stay code-managed.">
              <div className="space-y-2">
                {MEGA_MENU_KEYS.map((m) => {
                  const item = megaMenus[m.key] || { label: m.defaultLabel, enabled: true };
                  return (
                    <div key={m.key} className="flex items-center gap-3 flex-wrap p-3 rounded-lg" style={{ border: "1px solid var(--ax-border)" }}>
                      <ToggleField
                        label={m.defaultLabel}
                        checked={item.enabled !== false}
                        onChange={(v) => setMegaMenu(m.key, { enabled: v })}
                        testId={`cms-megamenu-enabled-${m.key}`}
                      />
                      <input
                        className="ax-input flex-1 min-w-[140px]"
                        value={item.label ?? m.defaultLabel}
                        onChange={(e) => setMegaMenu(m.key, { label: e.target.value })}
                        data-testid={`cms-megamenu-label-${m.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </Field>
          </>
        );
      }}
    />
  );
}

function AnnouncementBannerEditor() {
  return (
    <CmsSectionEditor
      page="global"
      section="announcement_banner"
      title="Announcement Banner"
      description="The scrolling strip below the header. Add, edit, reorder and toggle messages."
      renderFields={(draft, setField) => {
        const messages = Array.isArray(draft.messages) && draft.messages.length > 0
          ? draft.messages
          : [{ id: "m1", text: "No-cost EMI is available for all customers, subject to eligibility only", enabled: true }];

        const updateMsg = (i, patch) => {
          const next = messages.map((m, idx) => (idx === i ? { ...m, ...patch } : m));
          setField("messages", next);
        };
        const addMsg = () => setField("messages", [...messages, { id: `m${Date.now()}`, text: "", enabled: true }]);
        const removeMsg = (i) => setField("messages", messages.filter((_, idx) => idx !== i));
        const moveMsg = (i, dir) => {
          const j = i + dir;
          if (j < 0 || j >= messages.length) return;
          const next = [...messages];
          [next[i], next[j]] = [next[j], next[i]];
          setField("messages", next);
        };

        return (
          <>
            <ToggleField
              label="Announcement banner enabled"
              checked={draft.enabled !== false}
              onChange={(v) => setField("enabled", v)}
              testId="cms-banner-enabled"
            />
            <Field label="Messages">
              <div className="space-y-2">
                {messages.map((m, i) => (
                  <div key={m.id || i} className="flex items-center gap-2 flex-wrap p-3 rounded-lg" style={{ border: "1px solid var(--ax-border)" }}>
                    <input
                      className="ax-input flex-1 min-w-[220px]"
                      value={m.text}
                      onChange={(e) => updateMsg(i, { text: e.target.value })}
                      placeholder="Message text"
                      data-testid={`cms-banner-msg-text-${i}`}
                    />
                    <ToggleField label="On" checked={m.enabled !== false} onChange={(v) => updateMsg(i, { enabled: v })} testId={`cms-banner-msg-enabled-${i}`} />
                    <button type="button" className="ax-btn ax-btn-ghost" onClick={() => moveMsg(i, -1)} data-testid={`cms-banner-msg-up-${i}`}>
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" className="ax-btn ax-btn-ghost" onClick={() => moveMsg(i, 1)} data-testid={`cms-banner-msg-down-${i}`}>
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" className="ax-btn ax-btn-ghost" onClick={() => removeMsg(i)} data-testid={`cms-banner-msg-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button type="button" className="ax-btn ax-btn-ghost" onClick={addMsg} data-testid="cms-banner-add-message">
                  <Plus className="w-3.5 h-3.5" /> Add message
                </button>
              </div>
            </Field>
            <div className="grid sm:grid-cols-3 gap-4">
              <TextField label="Scroll speed (seconds per loop)" value={draft.speed_seconds ?? 45} onChange={(v) => setField("speed_seconds", v)} testId="cms-banner-speed" />
              <Field label="Direction">
                <select className="ax-select" value={draft.direction || "left"} onChange={(e) => setField("direction", e.target.value)} data-testid="cms-banner-direction">
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
              </Field>
              <TextField label="Separator" value={draft.separator ?? "·"} onChange={(v) => setField("separator", v)} testId="cms-banner-separator" />
            </div>
            <EmiTickerPreview messages={messages} speedSeconds={draft.speed_seconds} direction={draft.direction} separator={draft.separator} />
          </>
        );
      }}
    />
  );
}

function FooterEditor() {
  return (
    <CmsSectionEditor
      page="global"
      section="footer"
      title="Footer"
      description="Footer tagline and copyright text. Contact details are managed under the Contact tab (shared everywhere)."
      renderFields={(draft, setField) => (
        <>
          <TextAreaField label="Footer tagline" value={draft.tagline} onChange={(v) => setField("tagline", v)} testId="cms-footer-tagline" />
          <TextField
            label="Copyright text"
            value={draft.copyright_text}
            onChange={(v) => setField("copyright_text", v)}
            testId="cms-footer-copyright"
            placeholder="© {year} GenieX. All rights reserved. Intelligence at Your Command.™"
            hint="Use {year} anywhere you want the current year inserted."
          />
        </>
      )}
    />
  );
}

function ContactEditor() {
  return (
    <CmsSectionEditor
      page="global"
      section="contact"
      title="Contact & Company Information"
      description="A single source of truth — used in the header, footer and contact page."
      renderFields={(draft, setField) => (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Phone number" value={draft.phone} onChange={(v) => setField("phone", v)} testId="cms-contact-phone" placeholder="+91 90636 62999" />
            <TextField label="Email address" value={draft.email} onChange={(v) => setField("email", v)} testId="cms-contact-email" placeholder="hello@geniex.in" />
            <TextField label="WhatsApp number" value={draft.whatsapp} onChange={(v) => setField("whatsapp", v)} testId="cms-contact-whatsapp" />
            <TextField label="Business hours" value={draft.business_hours} onChange={(v) => setField("business_hours", v)} testId="cms-contact-hours" placeholder="Mon – Sat · 10 AM – 8 PM IST" />
          </div>
          <TextAreaField label="Company address" value={draft.address} onChange={(v) => setField("address", v)} testId="cms-contact-address" />
          <div className="grid sm:grid-cols-2 gap-4">
            {["instagram", "facebook", "youtube", "linkedin"].map((k) => (
              <TextField
                key={k}
                label={`${k[0].toUpperCase()}${k.slice(1)} URL`}
                value={draft.social?.[k]}
                onChange={(v) => setField("social", { ...(draft.social || {}), [k]: v })}
                testId={`cms-contact-social-${k}`}
              />
            ))}
          </div>
        </>
      )}
    />
  );
}

/* ---------------------------------------------------------------------- */
/* HOME                                                                    */
/* ---------------------------------------------------------------------- */

function HomeTab() {
  const [sub, setSub] = useState("hero");
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        {["hero", "sections"].map((s) => (
          <button
            key={s}
            type="button"
            className={`ax-btn ${sub === s ? "ax-btn-gold" : "ax-btn-ghost"}`}
            onClick={() => setSub(s)}
            data-testid={`cms-subtab-home-${s}`}
          >
            {s === "hero" ? "Hero Section" : "Section Visibility"}
          </button>
        ))}
      </div>
      {sub === "hero" && <HomeHeroEditor />}
      {sub === "sections" && <HomeSectionsEditor />}
    </div>
  );
}

function HomeHeroEditor() {
  return (
    <CmsSectionEditor
      page="home"
      section="hero"
      title="Home Hero"
      description="The full-bleed hero at the top of the homepage."
      renderFields={(draft, setField) => (
        <>
          <ToggleField label="Show hero section" checked={draft.visible !== false} onChange={(v) => setField("visible", v)} testId="cms-hero-visible" />
          <TextField label="Eyebrow / intro text" value={draft.eyebrow} onChange={(v) => setField("eyebrow", v)} testId="cms-hero-eyebrow" placeholder="Introducing GenieX" />
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Main heading" value={draft.heading_line1} onChange={(v) => setField("heading_line1", v)} testId="cms-hero-heading1" placeholder="Intelligence" />
            <TextField label="Highlighted / accent heading" value={draft.heading_accent} onChange={(v) => setField("heading_accent", v)} testId="cms-hero-heading-accent" placeholder="At Your Command." />
          </div>
          <TextAreaField label="Description" value={draft.description} onChange={(v) => setField("description", v)} testId="cms-hero-description" />
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Primary CTA text" value={draft.primary_cta_label} onChange={(v) => setField("primary_cta_label", v)} testId="cms-hero-primary-label" placeholder="Book a Consultation" />
            <TextField label="Primary CTA link (optional — blank opens the consultation dialog)" value={draft.primary_cta_link} onChange={(v) => setField("primary_cta_link", v)} testId="cms-hero-primary-link" />
            <TextField label="Secondary CTA text" value={draft.secondary_cta_label} onChange={(v) => setField("secondary_cta_label", v)} testId="cms-hero-secondary-label" placeholder="Explore the Experience" />
            <TextField label="Secondary CTA link (optional)" value={draft.secondary_cta_link} onChange={(v) => setField("secondary_cta_link", v)} testId="cms-hero-secondary-link" />
          </div>
          <ImageField label="Hero background — desktop" value={draft.desktop_image} onChange={(v) => setField("desktop_image", v)} testId="cms-hero-desktop-image" />
          <ImageField label="Hero background — mobile (optional)" value={draft.mobile_image} onChange={(v) => setField("mobile_image", v)} testId="cms-hero-mobile-image" hint="Falls back to the desktop image if left blank." />
        </>
      )}
    />
  );
}

const HOME_SECTIONS = [
  { key: "dynamic_series", label: "Dynamic Series" },
  { key: "value_packs_teaser", label: "Smart Value Packs" },
  { key: "voice_assistant", label: "Voice Assistant" },
  { key: "intelligent_living", label: "Intelligent Living" },
  { key: "solutions_tiers", label: "Why GenieX / Solutions Tiers" },
  { key: "why_geniex", label: "Why GenieX" },
  { key: "testimonials", label: "Testimonials" },
];

function HomeSectionsEditor() {
  return (
    <CmsSectionEditor
      page="home"
      section="section_visibility"
      title="Home — Section Visibility"
      description="Temporarily hide a section from the homepage without deleting its content. The Hero section has its own toggle in the Hero editor."
      renderFields={(draft, setField) => (
        <div className="space-y-3">
          {HOME_SECTIONS.map((s) => (
            <ToggleField
              key={s.key}
              label={s.label}
              checked={draft[s.key] !== false}
              onChange={(v) => setField(s.key, v)}
              testId={`cms-section-visible-${s.key}`}
            />
          ))}
        </div>
      )}
    />
  );
}

/* ---------------------------------------------------------------------- */
/* PRODUCT PAGES (Dynamic Series / Solutions / Value Packs — hero editors) */
/* ---------------------------------------------------------------------- */

const PRODUCT_PAGES = [
  { key: "dynamic-series", label: "Dynamic Series" },
  { key: "solutions", label: "Solutions" },
  { key: "value-packs", label: "Smart Value Packs" },
];

function PageHeroEditor({ page, label }) {
  return (
    <CmsSectionEditor
      page={page}
      section="hero"
      title={`${label} — Hero`}
      description="The top intro section of this page. Detailed product/pack content stays code-managed for now."
      renderFields={(draft, setField) => (
        <>
          <ToggleField label="Show hero section" checked={draft.visible !== false} onChange={(v) => setField("visible", v)} testId={`cms-${page}-hero-visible`} />
          <TextField label="Eyebrow / intro text" value={draft.eyebrow} onChange={(v) => setField("eyebrow", v)} testId={`cms-${page}-hero-eyebrow`} />
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Main heading" value={draft.heading_line1} onChange={(v) => setField("heading_line1", v)} testId={`cms-${page}-hero-heading1`} />
            <TextField label="Highlighted / accent heading" value={draft.heading_accent} onChange={(v) => setField("heading_accent", v)} testId={`cms-${page}-hero-heading-accent`} />
          </div>
          <TextAreaField label="Description" value={draft.description} onChange={(v) => setField("description", v)} testId={`cms-${page}-hero-description`} />
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Primary CTA text" value={draft.primary_cta_label} onChange={(v) => setField("primary_cta_label", v)} testId={`cms-${page}-hero-primary-label`} />
            <TextField label="Primary CTA link" value={draft.primary_cta_link} onChange={(v) => setField("primary_cta_link", v)} testId={`cms-${page}-hero-primary-link`} />
            <TextField label="Secondary CTA text" value={draft.secondary_cta_label} onChange={(v) => setField("secondary_cta_label", v)} testId={`cms-${page}-hero-secondary-label`} />
            <TextField label="Secondary CTA link" value={draft.secondary_cta_link} onChange={(v) => setField("secondary_cta_link", v)} testId={`cms-${page}-hero-secondary-link`} />
          </div>
          <ImageField label="Hero image — desktop" value={draft.desktop_image} onChange={(v) => setField("desktop_image", v)} testId={`cms-${page}-hero-desktop-image`} />
          <ImageField label="Hero image — mobile (optional)" value={draft.mobile_image} onChange={(v) => setField("mobile_image", v)} testId={`cms-${page}-hero-mobile-image`} hint="Falls back to the desktop image if left blank." />
        </>
      )}
    />
  );
}

function ProductPagesTab() {
  const [active, setActive] = useState(PRODUCT_PAGES[0].key);
  const [sub, setSub] = useState("hero");
  const showPacksTab = active === "value-packs";
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {PRODUCT_PAGES.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`ax-btn ${active === p.key ? "ax-btn-gold" : "ax-btn-ghost"}`}
            onClick={() => {
              setActive(p.key);
              setSub("hero");
            }}
            data-testid={`cms-subtab-pages-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {showPacksTab && (
        <div className="flex flex-wrap gap-2 mb-6">
          <button type="button" className={`ax-btn ${sub === "hero" ? "ax-btn-gold" : "ax-btn-ghost"}`} onClick={() => setSub("hero")} data-testid="cms-subtab-vp-hero">
            Hero
          </button>
          <button type="button" className={`ax-btn ${sub === "packs" ? "ax-btn-gold" : "ax-btn-ghost"}`} onClick={() => setSub("packs")} data-testid="cms-subtab-vp-packs">
            Pack Pricing &amp; Inclusions
          </button>
        </div>
      )}
      {sub === "hero" && <PageHeroEditor key={active} page={active} label={PRODUCT_PAGES.find((p) => p.key === active).label} />}
      {sub === "packs" && showPacksTab && <ValuePacksEditor />}
    </div>
  );
}

const EMPTY_PACK = () => ({
  key: `pack-${Date.now()}`, badge: "For 2 BHK Home", title: "Smart Value Pack", sub: "",
  price_label: "₹49,999", original_price_label: "", gst_text: "", rooms: "", emi: "",
  highlight: false, visible: true, coverage: [{ room: "Living Room", items: "" }],
});

function ValuePacksEditor() {
  return (
    <CmsSectionEditor
      page="value-packs"
      section="packs"
      title="Value Packs — Pricing &amp; Inclusions"
      description="Edit each pack's pricing and room-wise inclusions. Leave empty to keep the original 2 packs live on the site."
      renderFields={(draft, setField) => {
        const packs = Array.isArray(draft.packs) ? draft.packs : [];
        const updatePack = (i, patch) => setField("packs", packs.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
        const addPack = () => setField("packs", [...packs, EMPTY_PACK()]);
        const removePack = (i) => setField("packs", packs.filter((_, idx) => idx !== i));
        const movePack = (i, dir) => {
          const j = i + dir;
          if (j < 0 || j >= packs.length) return;
          const next = [...packs];
          [next[i], next[j]] = [next[j], next[i]];
          setField("packs", next);
        };
        const updateCoverage = (pi, ci, patch) => {
          const coverage = packs[pi].coverage.map((c, idx) => (idx === ci ? { ...c, ...patch } : c));
          updatePack(pi, { coverage });
        };
        const addCoverage = (pi) => updatePack(pi, { coverage: [...(packs[pi].coverage || []), { room: "", items: "" }] });
        const removeCoverage = (pi, ci) => updatePack(pi, { coverage: packs[pi].coverage.filter((_, idx) => idx !== ci) });

        return (
          <>
            {packs.length === 0 && (
              <p className="text-sm" style={{ color: "var(--ax-text-muted)" }}>
                No custom packs yet — the site is showing the original 2 BHK / 3 BHK packs. Click "Add pack" to start managing them here.
              </p>
            )}
            <div className="space-y-4">
              {packs.map((p, i) => (
                <div key={p.key || i} className="p-4 rounded-xl space-y-3" style={{ border: "1px solid var(--ax-border)" }} data-testid={`cms-pack-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <ToggleField label="Visible" checked={p.visible !== false} onChange={(v) => updatePack(i, { visible: v })} testId={`cms-pack-visible-${i}`} />
                    <ToggleField label="Highlight (Most Chosen)" checked={!!p.highlight} onChange={(v) => updatePack(i, { highlight: v })} testId={`cms-pack-highlight-${i}`} />
                    <div className="flex-1" />
                    <button type="button" className="ax-btn ax-btn-ghost" onClick={() => movePack(i, -1)}><ArrowUp className="w-3.5 h-3.5" /></button>
                    <button type="button" className="ax-btn ax-btn-ghost" onClick={() => movePack(i, 1)}><ArrowDown className="w-3.5 h-3.5" /></button>
                    <button type="button" className="ax-btn ax-btn-ghost" onClick={() => removePack(i)} data-testid={`cms-pack-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /> Remove</button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <TextField label="Badge (e.g. For 2 BHK Home)" value={p.badge} onChange={(v) => updatePack(i, { badge: v })} testId={`cms-pack-badge-${i}`} />
                    <TextField label="Pack title" value={p.title} onChange={(v) => updatePack(i, { title: v })} testId={`cms-pack-title-${i}`} />
                    <TextField label="Subtitle" value={p.sub} onChange={(v) => updatePack(i, { sub: v })} testId={`cms-pack-sub-${i}`} />
                    <TextField label="Rooms covered text" value={p.rooms} onChange={(v) => updatePack(i, { rooms: v })} testId={`cms-pack-rooms-${i}`} />
                    <TextField label="Price (display)" value={p.price_label} onChange={(v) => updatePack(i, { price_label: v })} testId={`cms-pack-price-${i}`} />
                    <TextField label="Original price (optional, shown struck-through)" value={p.original_price_label} onChange={(v) => updatePack(i, { original_price_label: v })} testId={`cms-pack-original-price-${i}`} />
                    <TextField label="GST / EMI note" value={p.emi} onChange={(v) => updatePack(i, { emi: v })} testId={`cms-pack-emi-${i}`} />
                    <TextField label="GST display text (optional)" value={p.gst_text} onChange={(v) => updatePack(i, { gst_text: v })} testId={`cms-pack-gst-${i}`} />
                  </div>
                  <Field label="Room-wise inclusions">
                    <div className="space-y-2">
                      {(p.coverage || []).map((c, ci) => (
                        <div key={ci} className="flex items-center gap-2 flex-wrap">
                          <input className="ax-input flex-1 min-w-[120px]" placeholder="Room" value={c.room} onChange={(e) => updateCoverage(i, ci, { room: e.target.value })} data-testid={`cms-pack-${i}-coverage-room-${ci}`} />
                          <input className="ax-input flex-[2] min-w-[200px]" placeholder="Items included" value={c.items} onChange={(e) => updateCoverage(i, ci, { items: e.target.value })} data-testid={`cms-pack-${i}-coverage-items-${ci}`} />
                          <button type="button" className="ax-btn ax-btn-ghost" onClick={() => removeCoverage(i, ci)}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                      <button type="button" className="ax-btn ax-btn-ghost" onClick={() => addCoverage(i)} data-testid={`cms-pack-${i}-add-coverage`}>
                        <Plus className="w-3.5 h-3.5" /> Add room
                      </button>
                    </div>
                  </Field>
                </div>
              ))}
            </div>
            <button type="button" className="ax-btn ax-btn-ghost" onClick={addPack} data-testid="cms-pack-add">
              <Plus className="w-3.5 h-3.5" /> Add pack
            </button>
          </>
        );
      }}
    />
  );
}

/* ---------------------------------------------------------------------- */
/* JOURNAL / BLOG CMS                                                      */
/* ---------------------------------------------------------------------- */

const BLOG_CATEGORY_OPTIONS = [
  { slug: "smart-home-guides", label: "Smart Home Guides" },
  { slug: "for-builders", label: "For Builders" },
  { slug: "location-insights", label: "Location Insights" },
  { slug: "case-studies", label: "Case Studies" },
];

const EMPTY_ARTICLE = {
  title: "", excerpt: "", category: "smart-home-guides", sections: [{ heading: "", body: "" }],
  keywords: "", featured_image: "", author: "GenieX Team", seo_title: "", seo_description: "", og_image: "",
};

function JournalTab() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi.get("/blog").then((r) => setArticles(r.data.articles || [])).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this article permanently?")) return;
    try {
      await adminApi.delete(`/blog/${id}`);
      toast.success("Article deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const togglePublish = async (a) => {
    try {
      await adminApi.post(`/blog/${a.id}/${a.status === "published" ? "unpublish" : "publish"}`);
      toast.success(a.status === "published" ? "Unpublished" : "Published — live on /blog");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  if (editingId || creating) {
    return (
      <ArticleEditor
        articleId={editingId}
        onClose={() => {
          setEditingId(null);
          setCreating(false);
          load();
        }}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm" style={{ color: "var(--ax-text-muted)" }}>
          {articles.length} article{articles.length === 1 ? "" : "s"}
        </p>
        <button type="button" className="ax-btn ax-btn-gold" onClick={() => setCreating(true)} data-testid="journal-new-article">
          <Plus className="w-3.5 h-3.5" /> New article
        </button>
      </div>
      {loading ? (
        <div className="text-sm py-10 text-center" style={{ color: "var(--ax-text-muted)" }}>Loading…</div>
      ) : articles.length === 0 ? (
        <div className="ax-glass p-10 text-center text-sm" style={{ color: "var(--ax-text-muted)" }}>
          No articles yet. Create your first one.
        </div>
      ) : (
        <div className="space-y-2">
          {articles.map((a) => (
            <div key={a.id} className="ax-glass p-4 flex items-center gap-4 flex-wrap" data-testid={`journal-article-row-${a.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{a.title || "(untitled)"}</span>
                  <span className={`ax-chip ${a.status === "published" ? "ax-chip-success" : "ax-chip-warn"}`}>{a.status}</span>
                </div>
                <p className="text-xs mt-1" style={{ color: "var(--ax-text-muted)" }}>/blog/{a.slug}</p>
              </div>
              <button type="button" className="ax-btn ax-btn-ghost" onClick={() => togglePublish(a)} data-testid={`journal-toggle-publish-${a.id}`}>
                {a.status === "published" ? <X className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {a.status === "published" ? "Unpublish" : "Publish"}
              </button>
              <button type="button" className="ax-btn ax-btn-ghost" onClick={() => setEditingId(a.id)} data-testid={`journal-edit-${a.id}`}>
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              <button type="button" className="ax-btn ax-btn-ghost" onClick={() => remove(a.id)} data-testid={`journal-delete-${a.id}`}>
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleEditor({ articleId, onClose }) {
  const [form, setForm] = useState(EMPTY_ARTICLE);
  const [loading, setLoading] = useState(!!articleId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (articleId) {
      adminApi.get(`/blog/${articleId}`).then((r) => setForm(r.data)).finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSection = (i, patch) => setForm((f) => ({ ...f, sections: f.sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) }));
  const addSection = () => setForm((f) => ({ ...f, sections: [...f.sections, { heading: "", body: "" }] }));
  const removeSection = (i) => setForm((f) => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));
  const moveSection = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.sections.length) return;
    const next = [...form.sections];
    [next[i], next[j]] = [next[j], next[i]];
    setF("sections", next);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || undefined };
      if (articleId) {
        await adminApi.put(`/blog/${articleId}`, payload);
      } else {
        await adminApi.post("/blog", payload);
      }
      toast.success("Article saved");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm py-10 text-center" style={{ color: "var(--ax-text-muted)" }}>Loading…</div>;
  }

  return (
    <div className="ax-glass p-6 lg:p-7">
      <div className="flex items-center justify-between mb-6">
        <h3 className="ax-serif text-2xl">{articleId ? "Edit article" : "New article"}</h3>
        <div className="flex gap-2">
          <button type="button" className="ax-btn ax-btn-ghost" onClick={onClose} data-testid="journal-editor-cancel">
            Cancel
          </button>
          <button type="button" className="ax-btn ax-btn-gold" onClick={save} disabled={saving} data-testid="journal-editor-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <div className="space-y-5">
        <TextField label="Title" value={form.title} onChange={(v) => setF("title", v)} testId="journal-field-title" />
        <TextAreaField label="Excerpt" value={form.excerpt} onChange={(v) => setF("excerpt", v)} testId="journal-field-excerpt" />
        <Field label="Category">
          <select className="ax-select" value={form.category} onChange={(e) => setF("category", e.target.value)} data-testid="journal-field-category">
            {BLOG_CATEGORY_OPTIONS.map((c) => (
              <option key={c.slug} value={c.slug}>{c.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Article content" hint="Add heading + paragraph blocks. Paragraphs support **bold**, [link text](https://...) and ![alt text](image-url) inline.">
          <div className="space-y-3">
            {form.sections.map((s, i) => (
              <div key={i} className="p-3 rounded-lg space-y-2" style={{ border: "1px solid var(--ax-border)" }}>
                <div className="flex items-center gap-2">
                  <input className="ax-input flex-1" placeholder="Heading" value={s.heading} onChange={(e) => setSection(i, { heading: e.target.value })} data-testid={`journal-section-heading-${i}`} />
                  <button type="button" className="ax-btn ax-btn-ghost" onClick={() => moveSection(i, -1)}><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button type="button" className="ax-btn ax-btn-ghost" onClick={() => moveSection(i, 1)}><ArrowDown className="w-3.5 h-3.5" /></button>
                  <button type="button" className="ax-btn ax-btn-ghost" onClick={() => removeSection(i)}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <textarea className="ax-input" style={{ height: "auto" }} rows={3} placeholder="Paragraph text" value={s.body} onChange={(e) => setSection(i, { body: e.target.value })} data-testid={`journal-section-body-${i}`} />
              </div>
            ))}
            <button type="button" className="ax-btn ax-btn-ghost" onClick={addSection} data-testid="journal-add-section">
              <Plus className="w-3.5 h-3.5" /> Add section
            </button>
          </div>
        </Field>
        <TextField label="Keywords" value={form.keywords} onChange={(v) => setF("keywords", v)} testId="journal-field-keywords" />
        <TextField label="Author" value={form.author} onChange={(v) => setF("author", v)} testId="journal-field-author" />
        <ImageField label="Featured image" value={form.featured_image} onChange={(v) => setF("featured_image", v)} testId="journal-field-featured-image" />
        <div className="pt-4" style={{ borderTop: "1px solid var(--ax-border)" }}>
          <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: "var(--ax-text-muted)" }}>SEO</p>
          <TextField label="SEO title" value={form.seo_title} onChange={(v) => setF("seo_title", v)} testId="journal-field-seo-title" />
          <TextAreaField label="Meta description" value={form.seo_description} onChange={(v) => setF("seo_description", v)} testId="journal-field-seo-description" />
          <ImageField label="Open Graph image" value={form.og_image} onChange={(v) => setF("og_image", v)} testId="journal-field-og-image" />
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* SEO & METADATA                                                          */
/* ---------------------------------------------------------------------- */

const SEO_PAGES = [
  { key: "home", label: "Home", path: "/" },
  { key: "dynamic-series", label: "Dynamic Series", path: "/dynamic-series" },
  { key: "value-packs", label: "Smart Value Packs", path: "/value-packs" },
  { key: "shop", label: "Shop", path: "/shop" },
  { key: "solutions", label: "Solutions", path: "/solutions" },
  { key: "about", label: "About", path: "/about" },
  { key: "contact", label: "Contact", path: "/contact" },
];

function SeoTab() {
  const [active, setActive] = useState(SEO_PAGES[0].key);
  const page = SEO_PAGES.find((p) => p.key === active);
  return (
    <div className="grid lg:grid-cols-[220px_1fr] gap-6">
      <div className="space-y-1">
        {SEO_PAGES.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setActive(p.key)}
            data-testid={`cms-seo-page-${p.key}`}
            className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${active === p.key ? "ax-chip-gold" : ""}`}
            style={{
              border: "1px solid var(--ax-border)",
              background: active === p.key ? "rgba(212,175,110,0.08)" : "transparent",
              color: active === p.key ? "var(--ax-gold)" : "var(--ax-text-dim)",
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <CmsSectionEditor
        key={active}
        page="seo"
        section={active}
        title={`SEO — ${page.label}`}
        description={`Overrides the built-in metadata for ${page.path}.`}
        renderFields={(draft, setField) => (
          <>
            <TextField label="Page title" value={draft.title} onChange={(v) => setField("title", v)} testId="cms-seo-title" />
            <TextAreaField label="Meta description" value={draft.description} onChange={(v) => setField("description", v)} testId="cms-seo-description" />
            <TextField label="Keywords" value={draft.keywords} onChange={(v) => setField("keywords", v)} testId="cms-seo-keywords" />
            <TextField label="Canonical URL" value={draft.canonical} onChange={(v) => setField("canonical", v)} testId="cms-seo-canonical" />
            <TextField label="Open Graph title" value={draft.og_title} onChange={(v) => setField("og_title", v)} testId="cms-seo-og-title" />
            <TextAreaField label="Open Graph description" value={draft.og_description} onChange={(v) => setField("og_description", v)} testId="cms-seo-og-description" />
            <ImageField label="Open Graph image" value={draft.og_image} onChange={(v) => setField("og_image", v)} testId="cms-seo-og-image" />
            <ToggleField label="No-index this page (hide from search engines)" checked={!!draft.noindex} onChange={(v) => setField("noindex", v)} testId="cms-seo-noindex" />
          </>
        )}
      />
    </div>
  );
}
